import { formattingMenuId } from "./menu-formatting";
import { applySharedMenu, type SharedMenuDefinition } from "./shared-menus";

/** Additive shared-header contract. Absence keeps the legacy shallow merge exactly. */
export type HeaderConfig = Record<string, any>;
export type HeaderPresentationMode = "overlay" | "solid";
export interface HeaderPresentation {
  mobile?: HeaderPresentationMode;
  desktop?: HeaderPresentationMode;
}
export interface SharedHeaderDefinition {
  id: string;
  name: string;
  config: HeaderConfig;
  logoUrl?: string;
  inverseLogoUrl?: string;
  colours?: Record<string, string>;
  menuSource?: "website" | "own";
  menuId?: string;
  actionsSource?: "website" | "own";
}
export interface HeaderSite {
  headers?: SharedHeaderDefinition[];
  menus?: SharedMenuDefinition[];
  layout?: { header?: HeaderConfig; [key: string]: any };
  [key: string]: any;
}
export const SHARED_HEADER_TYPES = [
  "DefaultHeader",
  "TransparentHeader",
  "FloatingHeader",
  "ClassicHeader",
];
export const headerRecord = (v: unknown): v is HeaderConfig =>
  !!v && typeof v === "object" && !Array.isArray(v);
export const copyHeaderValue = <T>(v: T): T => JSON.parse(JSON.stringify(v));
export function sharedHeaders(site: HeaderSite): SharedHeaderDefinition[] {
  return Array.isArray(site.headers)
    ? site.headers.filter(
        (h) =>
          h &&
          typeof h.id === "string" &&
          headerRecord(h.config) &&
          SHARED_HEADER_TYPES.includes(h.config.type),
      )
    : [];
}
export function headerPresentation(
  value: unknown,
): HeaderPresentation | undefined {
  if (!headerRecord(value)) return undefined;
  const valid = (v: unknown): v is HeaderPresentationMode =>
    v === "overlay" || v === "solid";
  const result: HeaderPresentation = {};
  if (valid(value.mobile)) result.mobile = value.mobile;
  if (valid(value.desktop)) result.desktop = value.desktop;
  return Object.keys(result).length ? result : undefined;
}
export interface ResolvedSharedHeader {
  config: HeaderConfig;
  base: HeaderConfig;
  page: HeaderConfig;
  definition?: SharedHeaderDefinition;
  presentation?: HeaderPresentation;
  shared: boolean;
}
export function resolveSharedHeader(
  site: HeaderSite,
  page: HeaderConfig = {},
): ResolvedSharedHeader {
  page = headerRecord(page) ? page : {};
  const root = headerRecord(site.layout?.header) ? site.layout!.header! : {};
  const available = sharedHeaders(site);
  const own = available.find((h) => h.id === page.id);
  const definition = own ?? available.find((h) => h.id === root.activeId);
  if (!definition)
    return {
      config: applySharedMenu(
        site,
        {
          type: "DefaultHeader",
          ...root,
          ...page,
          ...(site.menuFormatting
            ? {
                _formattingMenuId: formattingMenuId(
                  site,
                  page.menuId || page.navigation || page.classic?.navigation
                    ? page
                    : root,
                ),
              }
            : {}),
        },
        page.menuId ??
          (page.navigation || page.classic?.navigation
            ? undefined
            : root.menuId),
      ),
      base: root,
      page,
      definition,
      presentation: headerPresentation(page.presentation),
      shared: false,
    };
  const base: HeaderConfig = { type: "DefaultHeader", ...definition.config };
  // The website's menu/actions stay shared unless this design explicitly owns them.
  if (definition.menuSource !== "own") {
    delete base.navigation;
    base.classic = { ...base.classic };
    delete base.classic.navigation;
    if (Array.isArray(root.navigation)) base.navigation = root.navigation;
    else if (Array.isArray(root.classic?.navigation))
      base.classic.navigation = root.classic.navigation;
  }
  if (definition.actionsSource !== "own") {
    for (const key of ["actions", "ctas", "showSearch", "showB2c"]) {
      delete base[key];
      if (Object.prototype.hasOwnProperty.call(root, key))
        base[key] = root[key];
    }
    base.floating = { ...base.floating };
    if (Object.prototype.hasOwnProperty.call(root.floating ?? {}, "contact"))
      base.floating.contact = root.floating.contact;
    else delete base.floating.contact;
  }
  // Unconverted page overrides retain their old meaning; selecting a definition is explicit.
  const inline = own ? {} : { ...page };
  delete inline.id;
  delete inline.presentation;
  const config: HeaderConfig = { ...base, ...inline };
  const menuId =
    inline.menuId ??
    (inline.navigation || inline.classic?.navigation
      ? undefined
      : (definition.menuId ?? definition.config.menuId));
  return {
    config: {
      ...applySharedMenu(site, config, menuId),
      ...(site.menuFormatting
        ? {
            _formattingMenuId:
              menuId ??
              formattingMenuId(
                site,
                inline.navigation || inline.classic?.navigation
                  ? page
                  : { ...config, id: definition.id },
              ),
          }
        : {}),
    },
    base,
    page: inline,
    definition,
    presentation: headerPresentation(page.presentation),
    shared: true,
  };
}
export function applyHeaderPresentation(
  config: HeaderConfig,
  presentation: HeaderPresentation | undefined,
  desktop: boolean,
): HeaderConfig {
  const mode = desktop
    ? presentation?.desktop
    : (presentation?.mobile ?? presentation?.desktop);
  if (!mode || config.type === "DefaultHeader") return config;
  const result: HeaderConfig = { ...config, presentationMode: mode };
  if (config.type === "TransparentHeader") result.isVisible = mode === "solid";
  if (config.type === "ClassicHeader") {
    result.contentPlacement = mode === "overlay" ? "overlay" : "offset";
    result.classic = {
      ...config.classic,
      mobileOverlay: !desktop && mode === "overlay",
      solid: mode === "solid",
    };
    if (!desktop && mode === "overlay")
      result.contentPlacement = config.contentPlacement;
  }
  if (config.type === "FloatingHeader") {
    result.contentPlacement = mode === "overlay" ? "overlay" : "offset";
    if (mode === "solid")
      result.floating = {
        ...config.floating,
        surface: {
          ...config.floating?.surface,
          backgroundOpacity: 1,
          backdropBlur: 0,
        },
      };
  }
  return result;
}

const stable = (v: any): string =>
  JSON.stringify(v, (_key, value) =>
    headerRecord(value)
      ? Object.fromEntries(
          Object.keys(value)
            .sort()
            .map((k) => [k, value[k]]),
        )
      : value,
  );
/** Copy-only conversion preview. The caller explicitly applies it in one undo step. */
export function convertSharedHeaders<T extends HeaderSite>(
  input: T,
  id: () => string,
): T {
  const site = copyHeaderValue(input);
  if (sharedHeaders(site).length) return site;
  site.layout ??= {};
  const root = site.layout.header ?? {};
  const base: HeaderConfig = { type: "DefaultHeader", ...root };
  delete base.activeId;
  const main: SharedHeaderDefinition = {
    id: id(),
    name: "Hovedmenu",
    config: base,
    menuSource: "website",
    actionsSource: "website",
  };
  site.headers = [main];
  for (const page of site.pages ?? []) {
    if (
      page.type === "footer" ||
      !headerRecord(page.layout?.header) ||
      !Object.keys(page.layout.header).length
    )
      continue;
    const original = page.layout.header;
    const own = copyHeaderValue(original);
    const presentation: HeaderPresentation = {};
    const type = own.type ?? base.type;
    if (type === "TransparentHeader" && typeof own.isVisible === "boolean") {
      presentation.desktop = presentation.mobile = own.isVisible
        ? "solid"
        : "overlay";
      delete own.isVisible;
    }
    if (
      type === "ClassicHeader" &&
      own.classic?.mobileOverlay === true &&
      own.contentPlacement !== "overlay"
    ) {
      presentation.mobile = "overlay";
      delete own.classic.mobileOverlay;
      if (!Object.keys(own.classic).length) delete own.classic;
    }
    const config = { ...base, ...own };
    if (base.classic || own.classic)
      config.classic = { ...base.classic, ...own.classic };
    const fingerprint = stable(config);
    let definition = site.headers.find((h) => stable(h.config) === fingerprint);
    if (!definition) {
      definition = {
        id: id(),
        name: page.menuTitle || page.browserTitle || "Header",
        config,
        menuSource:
          own.navigation || own.classic?.navigation ? "own" : "website",
        actionsSource: own.actions ? "own" : "website",
      };
      site.headers.push(definition);
    }
    // Presentation-only pages keep following the website; explicit type selections stay pinned.
    page.layout.header = {
      ...(definition !== main || original.type !== undefined
        ? { id: definition.id }
        : {}),
      ...(Object.keys(presentation).length ? { presentation } : {}),
    };
  }
  site.layout.header = { ...root, activeId: main.id };
  syncSharedHeaderDefault(site);
  return site;
}
/** Derived legacy mirror, never run on reads or on sites without the opt-in collection. */
export function syncSharedHeaderDefault(site: HeaderSite): void {
  const root = site.layout?.header;
  const active = sharedHeaders(site).find((h) => h.id === root?.activeId);
  if (!root || !active) return;
  const common: HeaderConfig = {};
  for (const key of ["navigation", "actions", "ctas", "showSearch", "showB2c"])
    if (key in root) common[key] = root[key];
  const value = copyHeaderValue(active.config);
  if (active.menuId !== undefined) value.menuId = active.menuId;
  for (const key of ["navigation", "actions", "ctas", "showSearch", "showB2c"])
    delete value[key];
  if (value.classic) delete value.classic.navigation;
  if (value.floating) delete value.floating.contact;
  if (root.classic?.navigation)
    value.classic = { ...value.classic, navigation: root.classic.navigation };
  if (root.floating?.contact)
    value.floating = { ...value.floating, contact: root.floating.contact };
  site.layout!.header = { ...value, ...common, activeId: active.id };
}
